/**
 * CharacterCreationPage.jsx
 * 
 * Character Sheet Generator - Clear Background Focus
 * Consistent anime art book style with green screen output
 */

import React, { useState, useRef, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Upload, X, Wand2, Image as ImageIcon, User, Layers, Shield,
    Shirt, Camera, AlertCircle, Loader2, Sliders, ChevronDown, ChevronUp
} from 'lucide-react';
import { Spinner } from '../components/Animations';

// ==================== CONSTANTS ====================

const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const ALLOWED_FILE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

// Subject Parameters
const GENDER_OPTIONS = ['female', 'male', 'non-binary', 'futa', 'femboy'];
const BUILD_OPTIONS = ['petite athletic', 'curvy hourglass', 'athletic curves', 'muscular', 'plus-size', 'slender'];
const HAIR_COLORS = ['blonde', 'brown', 'black', 'red', 'silver', 'platinum', 'pink', 'blue', 'purple', 'rainbow ombre'];
const HAIR_LENGTHS = ['short', 'medium', 'long', 'very long'];
const HAIR_TEXTURES = ['straight', 'wavy', 'curly', 'braided', 'ponytail', 'pigtails'];
const EYE_COLORS = ['violet', 'emerald', 'bright blue', 'amber', 'grey', 'brown', 'hazel'];
const SKIN_TONES = ['pale', 'fair', 'medium', 'olive', 'tan', 'brown', 'dark', 'deep'];

// Wardrobe - Focused Options
const UPPER_COVERAGE = ['none', 'pasties', 'bra', 'bikini top', 'crop top', 'corset', 'bodysuit', 'harness only'];
const LOWER_COVERAGE = ['none', 'thong', 'panties', 'bikini bottom', 'micro skirt', 'garter belt only'];
const HARNESS_OPTIONS = ['none', 'leather chest harness', 'full body harness', 'rope bondage', 'chain harness'];
const LEGWEAR_OPTIONS = ['none', 'thigh-high stockings', 'fishnets', 'garter stockings', 'boots'];
const ACCESSORY_OPTIONS = ['choker', 'collar', 'cuffs', 'gloves', 'heels', 'ears', 'tail'];
const MATERIAL_OPTIONS = ['latex', 'leather', 'vinyl', 'lace', 'mesh', 'fishnet', 'sheer'];

// Pose - Character Sheet Focus
const POSE_OPTIONS = [
    { id: 'standing_neutral', name: 'Standing Neutral', icon: '🧍' },
    { id: 'standing_confident', name: 'Standing Confident', icon: '💪' },
    { id: 'hand_on_hip', name: 'Hand on Hip', icon: '✋' },
];

// Output Types
const OUTPUT_OPTIONS = [
    { id: 'front_back', name: 'Front + Back Views', desc: 'Standard character sheet' },
    { id: 'single_front', name: 'Front View Only', desc: 'Single portrait' },
    { id: 'four_angles', name: '4-Angle Turnaround', desc: 'Complete reference' },
];

// ==================== INITIAL STATE ====================

const INITIAL_SUBJECT = {
    gender: 'female',
    age: 25,
    build: 'curvy hourglass',
    hairColor: 'brown',
    hairLength: 'long',
    hairTexture: 'straight',
    eyeColor: 'bright blue',
    skinTone: 'fair',
    tattoos: false,
    piercings: false,
};

const INITIAL_WARDROBE = {
    upperCoverage: 'corset',
    lowerCoverage: 'thong',
    harness: 'none',
    legwear: 'thigh-high stockings',
    accessories: [],
    material: 'latex',
};

const INITIAL_SETTINGS = {
    pose: 'standing_neutral',
    outputType: 'front_back',
    guidance: 7.5,
    fidelity: 1.0,
};

// ==================== MAIN COMPONENT ====================

export default function CharacterCreationPage() {
    const [uploadedImage, setUploadedImage] = useState(null);
    const [uploadError, setUploadError] = useState(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const [generationError, setGenerationError] = useState(null);
    const [showAdvanced, setShowAdvanced] = useState(false);

    const [subject, setSubject] = useState({ ...INITIAL_SUBJECT });
    const [wardrobe, setWardrobe] = useState({ ...INITIAL_WARDROBE });
    const [settings, setSettings] = useState({ ...INITIAL_SETTINGS });

    const fileInputRef = useRef(null);

    const validateFile = useCallback((file) => {
        if (!file) return 'No file selected';
        if (!ALLOWED_FILE_TYPES.includes(file.type)) return 'Invalid file type';
        if (file.size > MAX_FILE_SIZE_BYTES) return `Max ${MAX_FILE_SIZE_MB}MB`;
        return null;
    }, []);

    const handleImageUpload = useCallback((e) => {
        const file = e.target.files?.[0];
        setUploadError(null);
        const error = validateFile(file);
        if (error) { setUploadError(error); return; }
        const reader = new FileReader();
        reader.onload = (event) => setUploadedImage(event.target?.result);
        reader.onerror = () => setUploadError('Failed to read file');
        reader.readAsDataURL(file);
    }, [validateFile]);

    const handleGenerate = useCallback(async () => {
        if (isGenerating) return;
        setIsGenerating(true);
        setGenerationError(null);

        // Character sheet payload - consistent style
        const payload = {
            // Fixed style settings
            style: {
                artStyle: 'anime_artbook',
                eyeStyle: 'large_anime_eyes',
                lineQuality: 'clean_cel_shaded',
                background: 'pure_green_00FF00',
                lighting: 'rim_lighting',
            },
            // Subject
            subject: {
                ...subject,
                extras: [subject.tattoos && 'tattoos', subject.piercings && 'piercings'].filter(Boolean)
            },
            // Wardrobe
            wardrobe,
            // Composition
            composition: {
                pose: settings.pose,
                outputType: settings.outputType,
                cameraAngle: 'full_body',
                feetVisible: true,
            },
            // Generation
            novelai: {
                sampler: 'k_euler_ancestral',
                guidance: settings.guidance,
                fidelity: settings.fidelity,
            },
            // Reference
            referenceImage: uploadedImage ? '[Base64]' : null,
        };

        try {
            await new Promise(resolve => setTimeout(resolve, 2000));
            console.log('Character Sheet Payload:', JSON.stringify(payload, null, 2));
        } catch { setGenerationError('Generation failed'); }
        finally { setIsGenerating(false); }
    }, [subject, wardrobe, settings, uploadedImage, isGenerating]);

    const clearImage = useCallback(() => {
        setUploadedImage(null);
        setUploadError(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    }, []);

    const updateSubject = useCallback((key, value) => setSubject(prev => ({ ...prev, [key]: value })), []);
    const updateWardrobe = useCallback((key, value) => setWardrobe(prev => ({ ...prev, [key]: value })), []);
    const updateSettings = useCallback((key, value) => setSettings(prev => ({ ...prev, [key]: value })), []);

    const toggleAccessory = useCallback((acc) => {
        setWardrobe(prev => ({
            ...prev,
            accessories: prev.accessories.includes(acc)
                ? prev.accessories.filter(a => a !== acc)
                : [...prev.accessories, acc]
        }));
    }, []);

    return (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
            {/* Header */}
            <header className="mb-8 text-center">
                <h1 className="text-3xl font-display font-semibold text-gray-100 mb-2">Character Sheet Generator</h1>
                <p className="text-gray-400">Create anime-style character references with clean green backgrounds</p>
            </header>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                {/* Left Column - Character Options */}
                <main className="lg:col-span-7 space-y-6">

                    {/* Reference Image */}
                    <SectionCard icon={Upload} title="Reference Image (Optional)" color="wine">
                        {uploadedImage ? (
                            <div className="relative group rounded-xl overflow-hidden border border-white/10 aspect-[3/2] bg-black/50 flex items-center justify-center">
                                <img src={uploadedImage} alt="Reference" className="max-h-full max-w-full object-contain" />
                                <button onClick={clearImage} className="absolute top-2 right-2 p-2 bg-black/70 rounded-full opacity-0 group-hover:opacity-100 hover:bg-red-500/80">
                                    <X size={16} className="text-white" />
                                </button>
                            </div>
                        ) : (
                            <div onClick={() => fileInputRef.current?.click()}
                                className={`border-2 border-dashed rounded-xl p-6 cursor-pointer flex flex-col items-center gap-2
                                    ${uploadError ? 'border-red-500/50 bg-red-500/5' : 'border-white/10 hover:border-wine-500/50 hover:bg-wine-500/5'}`}>
                                <Upload size={24} className={uploadError ? 'text-red-400' : 'text-gray-400'} />
                                <p className={`text-sm ${uploadError ? 'text-red-400' : 'text-gray-400'}`}>
                                    {uploadError || 'Upload a reference to guide character appearance'}
                                </p>
                            </div>
                        )}
                        <input type="file" ref={fileInputRef} className="hidden" accept={ALLOWED_FILE_TYPES.join(',')} onChange={handleImageUpload} />
                    </SectionCard>

                    {/* Subject Parameters */}
                    <SectionCard icon={User} title="Character" color="amber">
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                            <SelectField label="Gender" value={subject.gender} onChange={v => updateSubject('gender', v)} options={GENDER_OPTIONS} />
                            <SelectField label="Build" value={subject.build} onChange={v => updateSubject('build', v)} options={BUILD_OPTIONS} />
                            <div className="space-y-1">
                                <label className="text-xs font-medium text-gray-500 uppercase">Age</label>
                                <div className="flex items-center gap-2">
                                    <input type="range" min={18} max={60} value={subject.age} onChange={e => updateSubject('age', +e.target.value)}
                                        className="flex-1 h-2 bg-charcoal-900 rounded-lg accent-amber-500" />
                                    <span className="text-sm text-gray-300 w-8">{subject.age}</span>
                                </div>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <SelectField label="Hair Color" value={subject.hairColor} onChange={v => updateSubject('hairColor', v)} options={HAIR_COLORS} />
                            <SelectField label="Length" value={subject.hairLength} onChange={v => updateSubject('hairLength', v)} options={HAIR_LENGTHS} />
                            <SelectField label="Style" value={subject.hairTexture} onChange={v => updateSubject('hairTexture', v)} options={HAIR_TEXTURES} />
                            <SelectField label="Eyes" value={subject.eyeColor} onChange={v => updateSubject('eyeColor', v)} options={EYE_COLORS} />
                        </div>
                        <div className="mt-4 pt-4 border-t border-white/5 flex gap-4">
                            <SelectField label="Skin Tone" value={subject.skinTone} onChange={v => updateSubject('skinTone', v)} options={SKIN_TONES} />
                            <div className="flex items-end gap-2">
                                <TogglePill label="Tattoos" active={subject.tattoos} onClick={() => updateSubject('tattoos', !subject.tattoos)} />
                                <TogglePill label="Piercings" active={subject.piercings} onClick={() => updateSubject('piercings', !subject.piercings)} />
                            </div>
                        </div>
                    </SectionCard>

                    {/* Wardrobe */}
                    <SectionCard icon={Shirt} title="Outfit" color="emerald">
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                            <SelectField label="Upper" value={wardrobe.upperCoverage} onChange={v => updateWardrobe('upperCoverage', v)} options={UPPER_COVERAGE} />
                            <SelectField label="Lower" value={wardrobe.lowerCoverage} onChange={v => updateWardrobe('lowerCoverage', v)} options={LOWER_COVERAGE} />
                            <SelectField label="Harness" value={wardrobe.harness} onChange={v => updateWardrobe('harness', v)} options={HARNESS_OPTIONS} />
                        </div>
                        <div className="grid grid-cols-2 gap-4 mb-4">
                            <SelectField label="Legwear" value={wardrobe.legwear} onChange={v => updateWardrobe('legwear', v)} options={LEGWEAR_OPTIONS} />
                            <SelectField label="Material" value={wardrobe.material} onChange={v => updateWardrobe('material', v)} options={MATERIAL_OPTIONS} />
                        </div>
                        <div>
                            <label className="text-xs font-medium text-gray-500 uppercase mb-2 block">Accessories</label>
                            <div className="flex flex-wrap gap-2">
                                {ACCESSORY_OPTIONS.map(acc => (
                                    <TogglePill key={acc} label={acc} active={wardrobe.accessories.includes(acc)} onClick={() => toggleAccessory(acc)} />
                                ))}
                            </div>
                        </div>
                    </SectionCard>
                </main>

                {/* Right Column - Output Settings */}
                <aside className="lg:col-span-5 space-y-6">

                    {/* Output Type */}
                    <SectionCard icon={Layers} title="Output" color="purple">
                        <div className="space-y-3">
                            {OUTPUT_OPTIONS.map(opt => (
                                <button key={opt.id} onClick={() => updateSettings('outputType', opt.id)}
                                    className={`w-full p-4 rounded-xl text-left transition-all ${settings.outputType === opt.id
                                        ? 'bg-purple-500/10 border border-purple-500/50'
                                        : 'bg-charcoal-900/50 border border-white/5 hover:border-white/10'}`}>
                                    <div className={`font-medium ${settings.outputType === opt.id ? 'text-purple-400' : 'text-gray-300'}`}>{opt.name}</div>
                                    <div className="text-xs text-gray-500">{opt.desc}</div>
                                </button>
                            ))}
                        </div>
                    </SectionCard>

                    {/* Pose */}
                    <SectionCard icon={Camera} title="Pose" color="pink">
                        <div className="flex flex-wrap gap-2">
                            {POSE_OPTIONS.map(pose => (
                                <button key={pose.id} onClick={() => updateSettings('pose', pose.id)}
                                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2
                                        ${settings.pose === pose.id
                                            ? 'bg-wine-500/20 text-wine-400 border border-wine-500/50'
                                            : 'bg-charcoal-900 text-gray-400 border border-white/5 hover:border-white/10'}`}>
                                    <span>{pose.icon}</span>
                                    {pose.name}
                                </button>
                            ))}
                        </div>
                    </SectionCard>

                    {/* Style Preview */}
                    <div className="bg-charcoal-800/50 border border-white/5 rounded-2xl p-4">
                        <div className="text-xs font-bold uppercase text-gray-500 mb-2">Fixed Style</div>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                            <div className="flex justify-between"><span className="text-gray-500">Art:</span><span className="text-gray-300">Anime Art Book</span></div>
                            <div className="flex justify-between"><span className="text-gray-500">Eyes:</span><span className="text-gray-300">Large Anime</span></div>
                            <div className="flex justify-between"><span className="text-gray-500">Lines:</span><span className="text-gray-300">Clean Cel-Shaded</span></div>
                            <div className="flex justify-between"><span className="text-gray-500">Background:</span><span className="text-emerald-400">#00FF00</span></div>
                        </div>
                    </div>

                    {/* Advanced Settings */}
                    <button onClick={() => setShowAdvanced(!showAdvanced)}
                        className="w-full flex items-center justify-between px-4 py-3 rounded-xl bg-charcoal-800/30 border border-white/5 text-gray-500 hover:text-gray-300">
                        <span className="text-sm flex items-center gap-2"><Sliders size={14} /> Advanced</span>
                        {showAdvanced ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </button>
                    <AnimatePresence>
                        {showAdvanced && (
                            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
                                className="bg-charcoal-800/50 border border-white/5 rounded-xl p-4 space-y-3 overflow-hidden">
                                <SliderField label="Guidance" value={settings.guidance} min={5} max={12} step={0.5} onChange={v => updateSettings('guidance', v)} />
                                <SliderField label="Fidelity" value={settings.fidelity} min={0.5} max={1} step={0.1} onChange={v => updateSettings('fidelity', v)} />
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {/* Generate */}
                    <div className="sticky top-24">
                        {generationError && (
                            <div className="mb-3 p-3 rounded-lg bg-red-500/10 border border-red-500/30 flex items-center gap-2">
                                <AlertCircle size={14} className="text-red-400" />
                                <span className="text-xs text-red-400">{generationError}</span>
                            </div>
                        )}
                        <button onClick={handleGenerate} disabled={isGenerating}
                            className={`w-full py-4 rounded-xl font-bold text-lg shadow-xl transition-all flex items-center justify-center gap-3
                                ${isGenerating ? 'bg-charcoal-700 text-gray-500 cursor-not-allowed'
                                    : 'bg-gradient-to-r from-wine-600 to-amber-600 hover:from-wine-500 hover:to-amber-500 text-white hover:-translate-y-0.5'}`}>
                            {isGenerating ? <><Spinner size="sm" /> Generating...</> : <><Wand2 size={22} /> Generate Character</>}
                        </button>
                        <p className="text-center text-xs text-gray-500 mt-3">
                            {settings.outputType === 'front_back' && 'Front + Back • ~30 seconds'}
                            {settings.outputType === 'single_front' && 'Single view • ~15 seconds'}
                            {settings.outputType === 'four_angles' && '4 angles • ~60 seconds'}
                        </p>
                        <div className="mt-3 p-2.5 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-center">
                            <span className="text-xs text-emerald-400">✓ Green screen background • Ready for compositing</span>
                        </div>
                    </div>
                </aside>
            </div>
        </div>
    );
}

// ==================== SUB-COMPONENTS ====================

function SectionCard({ children, icon: Icon, title, color }) {
    const colors = { wine: 'text-wine-400', amber: 'text-amber-400', purple: 'text-purple-400', emerald: 'text-emerald-400', pink: 'text-pink-400' };
    return (
        <section className="bg-charcoal-800/50 border border-white/5 rounded-2xl p-5">
            <h2 className="text-base font-medium text-gray-200 mb-4 flex items-center gap-2">
                <Icon size={18} className={colors[color] || 'text-gray-400'} /> {title}
            </h2>
            {children}
        </section>
    );
}

function SelectField({ label, value, onChange, options }) {
    return (
        <div className="space-y-1">
            <label className="text-xs font-medium text-gray-500 uppercase">{label}</label>
            <select value={value} onChange={e => onChange(e.target.value)}
                className="w-full bg-charcoal-900 border border-white/10 rounded-lg px-3 py-2 text-sm text-gray-200 focus:outline-none focus:border-wine-500/50">
                {options.map(opt => <option key={opt} value={opt}>{opt.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}</option>)}
            </select>
        </div>
    );
}

function TogglePill({ label, active, onClick }) {
    return (
        <button onClick={onClick} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all
            ${active ? 'bg-wine-500/20 text-wine-400 border border-wine-500/50' : 'bg-charcoal-900 text-gray-500 border border-white/5 hover:text-gray-300'}`}>
            {label}
        </button>
    );
}

function SliderField({ label, value, min, max, step, onChange }) {
    return (
        <div className="space-y-1">
            <div className="flex justify-between text-xs">
                <span className="text-gray-500 uppercase font-medium">{label}</span>
                <span className="text-gray-400">{value}</span>
            </div>
            <input type="range" min={min} max={max} step={step} value={value} onChange={e => onChange(+e.target.value)}
                className="w-full h-1.5 bg-charcoal-900 rounded-lg accent-amber-500" />
        </div>
    );
}
